import React, { useState, useRef, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye, Edit } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';
import app from '../../firebase.js';

// Import images
import edaplogo from "../../UNCR/img/edaplogo.png";
import unilislogo from "../../UNCR/img/unilislogo.png";
import rectorSig from "../../UNCR/img/rector_sig.png";
import '../../UNCR/aquidedroit.css';
import './AttestationDirection.css';

const AttestationDirectionTemplate = () => {
    const [formData, setFormData] = useState({
        // Director info
        titlePrefix: 'Pr', // Pr/Dr
        directeurNom: '',
        institutionType: 'Universite', // Universite / Centre de Recherche / Ecole / Institut d'enseignement Superieur
        institutionName: '',
        
        // Document type
        documentType: 'THESE', // THESE/MEMOIRE
        pastTense: 'dirigé', // dirigé/dirigée
        article: 'la', // la/le
        documentTypeText: 'thèse', // thèse/Memoire
        
        // Thesis/Memoire title
        titreTravail: '',
        
        // Student info
        etudiantNom: '',
        dateNaissance: '',
        lieuNaissance: '',
        pays: '',
        numeroEnregistrement: '',
        
        // Academic info
        diplomeType: 'doctorat (Ph.D)',
        domaine: '',
        option: '',
        
        // Date
        dateEmission: new Date().toISOString().split('T')[0],
        
        // Language
        language: 'Français + English'
    });
    const [documents, setDocuments] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const db = getDatabase(app);

    // Load documents from Firebase
    useEffect(() => {
        const documentsRef = ref(db, `attestation-direction/EDAP/`);
        onValue(documentsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const documentArray = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setDocuments(documentArray);
            }
        });
    }, [db]);

    // Generate QR Code
    useEffect(() => {
        const generateQRCode = async () => {
            if (!selectedDocument) return;
            
            let qrData = `ATTESTATION DE DIRECTION DE THESE/MEMOIRE\n`;
            qrData += `Director: ${selectedDocument.titlePrefix || ''} ${selectedDocument.directeurNom || ''}\n`;
            qrData += `Institution: ${selectedDocument.institutionType || ''} ${selectedDocument.institutionName || ''}\n`;
            qrData += `Document Type: ${selectedDocument.documentType || ''}\n`;
            qrData += `Title: ${selectedDocument.titreTravail || ''}\n`;
            qrData += `Student: ${selectedDocument.etudiantNom || ''}\n`;
            qrData += `Date of Birth: ${selectedDocument.dateNaissance || ''}\n`;
            qrData += `Place of Birth: ${selectedDocument.lieuNaissance || ''}, ${selectedDocument.pays || ''}\n`;
            qrData += `Registration No: ${selectedDocument.numeroEnregistrement || ''}\n`;
            qrData += `Degree: ${selectedDocument.diplomeType || ''}\n`;
            qrData += `Field: ${selectedDocument.domaine || ''}\n`;
            qrData += `Option: ${selectedDocument.option || ''}\n`;
            qrData += `Issue Date: ${selectedDocument.dateEmission || ''}\n`;
            qrData += `Status: Document Verified\n`;
            qrData += `Issued by: UNILIS - Université de Lisala`;
            
            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });
                setQrCodeData(qrCodeUrl);
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };
        if (selectedDocument) generateQRCode();
    }, [selectedDocument]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Handle dependent dropdowns
        if (name === 'documentType') {
            const updates = {
                documentType: value,
                pastTense: value === 'THESE' ? 'dirigé' : 'dirige',
                article: value === 'THESE' ? 'la' : 'le',
                documentTypeText: value === 'THESE' ? 'thèse' : 'Memoire'
            };
            setFormData({ ...formData, ...updates });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validate required fields
        const requiredFields = ['directeurNom', 'institutionName', 'titreTravail', 'etudiantNom', 'dateNaissance', 'lieuNaissance', 'pays', 'numeroEnregistrement', 'domaine', 'option', 'dateEmission'];
        for (const field of requiredFields) {
            if (!formData[field] || formData[field].trim() === '') {
                alert(`${field} est obligatoire!`);
                return;
            }
        }

        // Save to Firebase
        const documentRefPath = isEditing 
            ? ref(db, `attestation-direction/EDAP/${editingId}`)
            : ref(db, `attestation-direction/EDAP/${formData.etudiantNom.replace(/\s+/g, '_')}_${Date.now()}`);
        
        const dataToSave = isEditing 
            ? { ...formData }
            : { ...formData, createdAt: new Date().toISOString() };
        
        set(documentRefPath, dataToSave)
            .then(() => {
                console.log(isEditing ? 'Attestation direction updated successfully' : 'Attestation direction saved successfully');
                message.success(isEditing ? 'Document modifié avec succès' : 'Document créé avec succès');
                resetForm();
            })
            .catch((error) => {
                console.error('Error saving attestation direction:', error);
                message.error(isEditing ? 'Erreur lors de la modification' : 'Erreur lors de la création');
            });
    };

    const showDocumentModal = (document) => {
        setSelectedDocument(document);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedDocument(null);
    };

    const resetForm = () => {
        setFormData({
            titlePrefix: 'Pr',
            directeurNom: '',
            institutionType: 'Universite',
            institutionName: '',
            documentType: 'THESE',
            pastTense: 'dirigé',
            article: 'la',
            documentTypeText: 'thèse',
            titreTravail: '',
            etudiantNom: '',
            dateNaissance: '',
            lieuNaissance: '',
            pays: '',
            numeroEnregistrement: '',
            diplomeType: 'doctorat (Ph.D)',
            domaine: '',
            option: '',
            dateEmission: new Date().toISOString().split('T')[0],
            language: 'Français + English'
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEditDocument = (document) => {
        setFormData({
            titlePrefix: document.titlePrefix || 'Pr',
            directeurNom: document.directeurNom || '',
            institutionType: document.institutionType || 'Universite',
            institutionName: document.institutionName || '',
            documentType: document.documentType || 'THESE',
            pastTense: document.pastTense || (document.documentType === 'THESE' ? 'dirigé' : 'dirige'),
            article: document.article || (document.documentType === 'THESE' ? 'la' : 'le'),
            documentTypeText: document.documentTypeText || (document.documentType === 'THESE' ? 'thèse' : 'Memoire'),
            titreTravail: document.titreTravail || '',
            etudiantNom: document.etudiantNom || '',
            dateNaissance: document.dateNaissance || '',
            lieuNaissance: document.lieuNaissance || '',
            pays: document.pays || '',
            numeroEnregistrement: document.numeroEnregistrement || '',
            diplomeType: document.diplomeType || 'doctorat (Ph.D)',
            domaine: document.domaine || '',
            option: document.option || '',
            dateEmission: document.dateEmission || new Date().toISOString().split('T')[0],
            language: document.language || 'Français + English'
        });
        setIsEditing(true);
        setEditingId(document.id);
        
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteDocument = (id) => {
        remove(ref(db, `attestation-direction/EDAP/${id}`))
            .then(() => {
                message.success('Document supprimé avec succès');
            })
            .catch((error) => {
                console.error('Error deleting document:', error);
                message.error('Erreur lors de la suppression');
            });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const downloadPDF = async () => {
        if (!selectedDocument) return;

        const element = document.getElementById('attestation-direction-document');
        if (!element) {
            console.error('Document element not found');
            return;
        }

        try {
            const pdf = new jsPDF('p', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Get all document pages
            const pages = element.querySelectorAll('.aquidedroit-document');
            
            if (pages.length === 0) {
                message.error('Aucune page trouvée');
                return;
            }

            // Process each page individually (like bulletin.js)
            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                
                // Ensure white background for capture
                page.style.backgroundColor = 'white';

                // Wait for images to load
                const images = page.querySelectorAll('img');
                await Promise.all(Array.from(images).map(img => new Promise((resolve) => {
                    if (img.complete) resolve();
                    else { img.onload = resolve; img.onerror = resolve; }
                })));

                await new Promise(resolve => setTimeout(resolve, 400));

                const canvas = await html2canvas(page, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#FFFFFF',
                    windowWidth: page.scrollWidth,
                    windowHeight: page.scrollHeight
                });

                // Calculate scaling to fit page properly
                const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
                const w = canvas.width * ratio;
                const h = canvas.height * ratio;
                const x = (pageWidth - w) / 2; // Center horizontally
                const y = 20; // Small top margin

                if (i > 0) pdf.addPage();
                
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, w, h);
            }

            pdf.save(`${selectedDocument.etudiantNom?.replace(/\s+/g, '_') || 'document'}_Attestation_Direction.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Erreur lors de la génération du PDF');
        }
    };

    return (
        <div className="attestation-direction-container">
            <div className="attestation-direction-form-section">
                <div style={{ textAlign: 'center' }}>
                    <img src={edaplogo} alt="Logo EDAP" className="attestation-direction-form-logo" />
                    <h2>Formulaire d'Attestation de Direction de Thèse/Mémoire</h2>
                </div>

                <form onSubmit={handleSubmit} className="attestation-direction-form">
                    {/* Informations du directeur */}
                    <div className="attestation-direction-form-section-title">Informations du directeur</div>
                    <div className="attestation-direction-form-row">
                        <select
                            name="titlePrefix"
                            value={formData.titlePrefix}
                            onChange={handleInputChange}
                            required
                            className="attestation-direction-form-input"
                        >
                            <option value="Pr">Pr</option>
                            <option value="Dr">Dr</option>
                        </select>
                        <input
                            type="text"
                            name="directeurNom"
                            value={formData.directeurNom}
                            onChange={handleInputChange}
                            placeholder="Nom du directeur"
                            required
                            className="attestation-direction-form-input"
                        />
                    </div>

                    <div className="attestation-direction-form-row">
                        <select
                            name="institutionType"
                            value={formData.institutionType}
                            onChange={handleInputChange}
                            required
                            className="attestation-direction-form-input"
                        >
                            <option value="Universite">Université</option>
                            <option value="Centre de Recherche">Centre de Recherche</option>
                            <option value="Ecole">Ecole</option>
                            <option value="Institut d'enseignement Superieur">Institut d'enseignement Supérieur</option>
                        </select>
                        <input
                            type="text"
                            name="institutionName"
                            value={formData.institutionName}
                            onChange={handleInputChange}
                            placeholder="Nom de l'institution"
                            required
                            className="attestation-direction-form-input"
                        />
                    </div>

                    {/* Type de document */}
                    <div className="attestation-direction-form-section-title">Type de document</div>
                    <div className="attestation-direction-form-row">
                        <select
                            name="documentType"
                            value={formData.documentType}
                            onChange={handleInputChange}
                            required
                            className="attestation-direction-form-input"
                        >
                            <option value="THESE">THESE</option>
                            <option value="MEMOIRE">MEMOIRE</option>
                        </select>
                        <select
                            name="pastTense"
                            value={formData.pastTense}
                            onChange={handleInputChange}
                            required
                            className="attestation-direction-form-input"
                        >
                            <option value="dirigé">dirigé (passé)</option>
                            <option value="dirige">dirige (présent)</option>
                        </select>
                    </div>

                    {/* Informations du travail */}
                    <div className="attestation-direction-form-section-title">Informations du travail</div>
                    <div className="attestation-direction-form-row">
                        <textarea
                            name="titreTravail"
                            value={formData.titreTravail}
                            onChange={handleInputChange}
                            placeholder="Titre de la thèse/mémoire"
                            required
                            className="attestation-direction-form-input"
                            rows="3"
                            style={{ gridColumn: '1 / -1' }}
                        />
                    </div>

                    {/* Informations de l'étudiant */}
                    <div className="attestation-direction-form-section-title">Informations de l'étudiant</div>
                    <div className="attestation-direction-form-row">
                        <input
                            type="text"
                            name="etudiantNom"
                            value={formData.etudiantNom}
                            onChange={handleInputChange}
                            placeholder="Nom complet de l'étudiant"
                            required
                            className="attestation-direction-form-input"
                        />
                        <input
                            type="date"
                            name="dateNaissance"
                            value={formData.dateNaissance}
                            onChange={handleInputChange}
                            required
                            className="attestation-direction-form-input"
                        />
                    </div>

                    <div className="attestation-direction-form-row">
                        <input
                            type="text"
                            name="lieuNaissance"
                            value={formData.lieuNaissance}
                            onChange={handleInputChange}
                            placeholder="Lieu de naissance"
                            required
                            className="attestation-direction-form-input"
                        />
                        <input
                            type="text"
                            name="pays"
                            value={formData.pays}
                            onChange={handleInputChange}
                            placeholder="Pays"
                            required
                            className="attestation-direction-form-input"
                        />
                    </div>

                    <div className="attestation-direction-form-row">
                        <input
                            type="text"
                            name="numeroEnregistrement"
                            value={formData.numeroEnregistrement}
                            onChange={handleInputChange}
                            placeholder="Numéro d'enregistrement"
                            required
                            className="attestation-direction-form-input"
                        />
                    </div>

                    {/* Informations académiques */}
                    <div className="attestation-direction-form-section-title">Informations académiques</div>
                    <div className="attestation-direction-form-row">
                        <input
                            type="text"
                            name="diplomeType"
                            value={formData.diplomeType}
                            onChange={handleInputChange}
                            placeholder="Type de diplôme (ex: doctorat (Ph.D))"
                            required
                            className="attestation-direction-form-input"
                        />
                        <input
                            type="text"
                            name="domaine"
                            value={formData.domaine}
                            onChange={handleInputChange}
                            placeholder="Domaine (ex: Sciences Économique et de Gestion)"
                            required
                            className="attestation-direction-form-input"
                        />
                    </div>

                    <div className="attestation-direction-form-row">
                        <input
                            type="text"
                            name="option"
                            value={formData.option}
                            onChange={handleInputChange}
                            placeholder="Option (ex: Finance)"
                            required
                            className="attestation-direction-form-input"
                        />
                    </div>

                    {/* Informations du document */}
                    <div className="attestation-direction-form-section-title">Informations du document</div>
                    <div className="attestation-direction-form-row">
                        <input
                            type="date"
                            name="dateEmission"
                            value={formData.dateEmission}
                            onChange={handleInputChange}
                            required
                            className="attestation-direction-form-input"
                        />
                        <select
                            name="language"
                            value={formData.language}
                            onChange={handleInputChange}
                            required
                            className="attestation-direction-form-input"
                        >
                            <option value="Français + English">Français + English (2 pages)</option>
                            <option value="Français">Français (1 page)</option>
                            <option value="English">English (1 page)</option>
                        </select>
                    </div>

                    <button type="submit" className="attestation-direction-submit-btn">
                        {isEditing ? 'Modifier l\'Attestation' : 'Créer l\'Attestation'}
                    </button>
                    {isEditing && (
                        <button 
                            type="button" 
                            onClick={resetForm}
                            className="attestation-direction-submit-btn"
                            style={{ 
                                marginTop: '10px', 
                                background: 'linear-gradient(135deg, #6c757d, #5a6268)' 
                            }}
                        >
                            Annuler
                        </button>
                    )}
                </form>

                {/* Document List */}
                <div className="attestation-direction-document-list">
                    {documents.map((document) => (
                        <div key={document.id} className="attestation-direction-document-item">
                            <button
                                className="attestation-direction-document-toggle"
                                onClick={() => showDocumentModal(document)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {document.etudiantNom} - Attestation Direction
                            </button>
                            <button
                                className="attestation-direction-document-toggle"
                                onClick={() => handleEditDocument(document)}
                                style={{ 
                                    marginRight: '8px', 
                                    background: '#ffc107',
                                    color: '#000'
                                }}
                            >
                                <Edit size={16} style={{ marginRight: '8px' }} />
                                Modifier
                            </button>
                            <Popconfirm
                                title="Supprimer le document"
                                description="Êtes-vous sûr de vouloir supprimer ce document ?"
                                onConfirm={() => handleDeleteDocument(document.id)}
                                okText="Oui"
                                cancelText="Non"
                                okButtonProps={{ danger: true }}
                            >
                                <button className="attestation-direction-delete-btn">
                                    <Delete size={15} />
                                </button>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            <ConfigProvider>
                <Modal
                    title={<span style={{ color: 'white' }}>Aperçu - Attestation de Direction de Thèse/Mémoire</span>}
                    open={modalVisible}
                    onCancel={handleModalClose}
                    width="95vw"
                    style={{ top: 10 }}
                    centered={false}
                    maskClosable={true}
                    keyboard={true}
                    footer={[
                        <Button
                            key="download"
                            type="primary"
                            icon={<Download size={16} />}
                            onClick={downloadPDF}
                            style={{
                                backgroundColor: '#28a745',
                                borderColor: '#28a745',
                                fontWeight: 'bold'
                            }}
                        >
                            Télécharger PDF
                        </Button>,
                        <Button
                            key="close"
                            onClick={handleModalClose}
                            style={{ fontWeight: 'bold' }}
                        >
                            Fermer
                        </Button>
                    ]}
                >
                    {selectedDocument && (
                        <div className="attestation-direction-modal-content">
                            <AttestationDirectionDocument document={selectedDocument} qrCodeData={qrCodeData} language={selectedDocument.language || 'Français + English'} />
                        </div>
                    )}
                </Modal>
            </ConfigProvider>
        </div>
    );
};

// Document Component
const AttestationDirectionDocument = ({ document: docData, qrCodeData, language = 'Français + English' }) => {
    const showBoth = language === 'Français + English';
    const showFrench = language === 'Français' || showBoth;
    const showEnglish = language === 'English' || showBoth;

    return (
        <div id="attestation-direction-document" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'transparent',
            padding: '20px',
            width: 'fit-content',
            minWidth: '21cm',
            margin: '0 auto',
            overflowX: 'hidden'
        }}>
            {showFrench && (
                <AttestationDirectionPage document={docData} qrCodeData={qrCodeData} language="French" />
            )}
            {showEnglish && (
                <AttestationDirectionPage document={docData} qrCodeData={qrCodeData} language="English" />
            )}
        </div>
    );
};

// Single Page Component
const AttestationDirectionPage = ({ document: docData, qrCodeData, language = 'French' }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatDateWithMonthName = (dateStr, lang = 'fr') => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const monthsFr = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
        const monthsEn = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        const months = lang === 'en' ? monthsEn : monthsFr;
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const isEnglish = language === 'English';
    const pageId = `attestation-direction-${isEnglish ? 'english' : 'french'}`;

    // Translation mappings
    const translations = {
        'ATTESTATION DE DIRECTION DE THESE/MEMOIRE': isEnglish ? 'ATTESTATION OF THESIS/MEMOIRE SUPERVISION' : 'ATTESTATION DE DIRECTION DE THESE/MEMOIRE',
        'Nous attestons que:': isEnglish ? 'We certify that:' : 'Nous attestons que:',
        'rattachè(e) à l\'': isEnglish ? 'attached to the' : 'rattachè(e) à l\'',
        'au': isEnglish ? 'to the' : 'au',
        'a': isEnglish ? 'has' : 'a',
        'intitulė(e)': isEnglish ? 'entitled' : 'intitulė(e)',
        'Le dit travail appartient à l impėtrant': isEnglish ? 'This work belongs to ' : 'Le dit travail appartient à l impėtrant',
        'né(e) le': isEnglish ? 'born on' : 'né(e) le',
        'à': isEnglish ? 'in' : 'à',
        'enregistré(e) sous No': isEnglish ? 'registered under No' : 'enregistré(e) sous No',
        'pour l\'obtention du Diplôme de': isEnglish ? 'for the award of the Degree of' : 'pour l\'obtention du Diplôme de',
        'en': isEnglish ? 'in' : 'en',
        'Option :': isEnglish ? 'Option:' : 'Option :',
        'dans le cadre des accords de partenariats gestion conjointe des programmes et de cotutelle internationale de thèses et Codiplomation signés entre le Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) de l\'Université de Lisala en RDC, Université IRGIB AFRICA au Benin et UNCR University aux États-Unis;': isEnglish
            ? 'within the framework of partnership agreements for joint program management and international co-supervision of theses and Co-diplomation signed between the Interuniversity Center for Multidisciplinary Research (CIREP) of the University of Lisala in DRC, IRGIB AFRICA University in Benin and UNCR University in the United States;'
            : 'dans le cadre des accords de partenariats gestion conjointe des programmes et de cotutelle internationale de thèses et Codiplomation signés entre le Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) de l\'Université de Lisala en RDC, Université IRGIB AFRICA au Benin et UNCR University aux États-Unis;',
        'Cette attestation lui est delivė pour valoir  ce que de droit': isEnglish
            ? 'This attestation is issued to him/her for all legal purposes'
            : 'Cette attestation lui est delivė pour valoir  ce que de droit',
        'Fait a a Lisala le': isEnglish ? 'Done in Lisala on' : 'Fait a a Lisala le',
        'Recteur': isEnglish ? 'Rector' : 'Recteur'
    };

    const getInstitutionDisplay = () => {
        const type = docData.institutionType || 'Universite';
        if (isEnglish) {
            if (type === 'Universite') return { article: 'the', prefix: 'University', prep: 'of', show: 'the University of' };
            if (type === 'Centre de Recherche') return { article: 'the', prefix: 'Research Center', prep: 'of', show: 'the Research Center of' };
            if (type === 'Ecole') return { article: 'the', prefix: 'School', prep: 'of', show: 'the School of' };
            if (type === 'Institut d\'enseignement Superieur') return { article: 'the', prefix: 'Higher Education Institute', prep: 'of', show: 'the Higher Education Institute of' };
        }
        // French version
        if (type === 'Universite') return { article: 'l\'', prefix: 'Université', prep: 'de', show: 'l\' Universite' };
        if (type === 'Centre de Recherche') return { article: '(au)', prefix: 'Centre de Recherche', prep: 'de', show: '(au) Centre de Recherche' };
        if (type === 'Ecole') return { article: 'l\'', prefix: 'Ecole', prep: 'd\'', show: 'Ecole' };
        if (type === 'Institut d\'enseignement Superieur') return { article: 'l\'', prefix: 'Institut d\'enseignement Supérieur', prep: 'd\'', show: 'Institut d\'enseignement Superieur' };
        return { article: 'l\'', prefix: type, prep: 'de', show: type };
    };

    return (
        <div className="aquidedroit-document" id={pageId} style={language === 'French' && docData.language === 'Français + English' ? { pageBreakAfter: 'always' } : {}}>
            {/* Header (same as attestationGrade) */}
            <div className="aquidedroit-header">
                <div className="aquidedroit-header-row">
                    <div className="aquidedroit-left-logo">
                        <img src={unilislogo} alt="Logo UNILIS" className="aquidedroit-logo" />
                        <div className="aquidedroit-left-text">
                            <h1 className="aquidedroit-school">{isEnglish ? 'DOCTORAL SCHOOL' : 'ECOLE DOCTORALE'}</h1>
                            <h2 className="aquidedroit-subtitle">{isEnglish ? 'International Joint Supervision and Joint Degree' : 'Cotutelle Internationale de Thèse et Co-diplomation'}</h2>
                        </div>
                    </div>
                    <div className="aquidedroit-header-center">
                        <h3 className="aquidedroit-university">{isEnglish ? 'UNIVERSITY OF LISALA' : 'UNIVERSITE DE LISALA'}</h3>
                        <p className="aquidedroit-center">{isEnglish ? 'Interuniversity Center for Multidisciplinary Research (Cirep)' : 'Centre Interuniversitaire de Recherche Pluridisciplinaire (Cirep)'}</p>
                        <p className="aquidedroit-status">{isEnglish ? 'Status: Public University' : 'Status: Universite publique'}</p>
                        <p className="aquidedroit-web">Web: www.cirep.ac.cd</p>
                        <p className="aquidedroit-email">E-mail: info@cirep.ac.ad</p>
                    </div>
                    <div className="aquidedroit-right-logo">
                        <img src={edaplogo} alt="Logo EDAP" className="aquidedroit-logo" />
                    </div>
                </div>
            </div>

            {/* Separator */}
            <div className="aquidedroit-separator"></div>

            {/* Title */}
            <div className="aquidedroit-title-section">
                <h1 className="aquidedroit-main-title">
                    {isEnglish 
                        ? `ATTESTATION OF ${docData.documentType === 'THESE' ? 'THESIS' : 'MEMOIRE'} SUPERVISION`
                        : `ATTESTATION DE DIRECTION DE ${docData.documentType === 'THESE' ? 'THESE' : 'MEMOIRE'}`
                    }
                </h1>
            </div>

            {/* Body */}
            <div className="aquidedroit-document-body">
                <div className="aquidedroit-main-text">
                    <p style={{ textAlign: 'justify', lineHeight: 1.8 }}>
                        {isEnglish ? 'We certify that:' : 'Nous attestons que:'} <strong>{docData.titlePrefix || 'Pr'}</strong> <strong>{docData.directeurNom || ''}</strong> {isEnglish ? 'attached to' : 'rattachè(e) à'} {(() => {
                            const inst = getInstitutionDisplay();
                            if (isEnglish) {
                                return `${inst.article} ${inst.prefix} ${inst.prep}`;
                            } else {
                                // For French, show the format: l' Universite/ (au) Centre de Recherche/ Ecole/ Institut d'enseignement Superieur
                                return inst.show || `${inst.article} ${inst.prefix}`;
                            }
                        })()} {isEnglish ? '' : 'de'} <strong>{docData.institutionName || ''}</strong> {isEnglish 
                            ? (docData.pastTense === 'dirigé' ? 'has supervised' : 'is supervising')
                            : `a ${docData.pastTense || ''}`
                        } {isEnglish ? 'the thesis' : 'la thèse'} {isEnglish ? 'entitled' : 'intitulė(e)'}
                    </p>
                    <p style={{ textAlign: 'justify', lineHeight: 1.8, marginTop: '15px' }}>
                        <strong>"{docData.titreTravail || ''}"</strong>
                    </p>
                    <p style={{ textAlign: 'justify', lineHeight: 1.8, marginTop: '15px' }}>
                        {isEnglish ? 'This dissertation belongs to ' : 'Le dit travail appartient à l impėtrant'} <strong>{docData.etudiantNom || ''}</strong>, {isEnglish ? 'born on' : 'né(e) le'} <strong>{formatDate(docData.dateNaissance)}</strong> {isEnglish ? 'in' : 'à'} <strong>{docData.lieuNaissance || ''}</strong>, <strong>{docData.pays || ''}</strong>, {isEnglish ? 'registered under No' : 'enregistré(e) sous No'} <strong>{docData.numeroEnregistrement || ''}</strong> {isEnglish ? 'for the award of the Degree of' : 'pour l\'obtention du Diplôme de'} <strong>{docData.diplomeType || ''}</strong> {isEnglish ? 'in' : 'en'} <strong>{docData.domaine || ''}</strong>, {isEnglish ? 'Option:' : 'Option :'} <strong>{docData.option || ''}</strong>, {isEnglish 
                            ? 'within the framework of partnership agreements for joint program management and international co-supervision of theses and Co-diplomation signed between the Interuniversity Center for Multidisciplinary Research (CIREP) of the University of Lisala in DRC, IRGIB AFRICA University in Benin and UNCR University in the United States;'
                            : 'dans le cadre des accords de partenariats gestion conjointe des programmes et de cotutelle internationale de thèses et Codiplomation signés entre le Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) de l\'Université de Lisala en RDC, Université IRGIB AFRICA au Benin et UNCR University aux États-Unis;'}
                    </p>
                    <p style={{ textAlign: 'justify', lineHeight: 1.8, marginTop: '15px' }}>
                        {isEnglish ? 'This attestation is issued to him/her for all legal purposes.' : 'Cette attestation lui est delivė pour valoir  ce que de droit.'}
                    </p>
                </div>
            </div>

            {/* Signatures and QR Code */}
            <div className="aquidedroit-signature" style={{ marginTop: '40px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <p style={{ textAlign: 'left', marginBottom: '20px', lineHeight: 1.8 }}>
                        {isEnglish ? 'Done in Lisala on' : 'Fait a a Lisala le'} <strong>{formatDateWithMonthName(docData.dateEmission, isEnglish ? 'en' : 'fr')}</strong>
                    </p>
                    <div className="aquidedroit-signature-content" style={{ textAlign: 'left' }}>
                        <img 
                            src={rectorSig} 
                            alt="Signature" 
                            className="aquidedroit-signature-image"
                        />
                        <hr style={{ width: '200px', margin: '0 auto 8px 0', border: 'none', borderTop: '1px solid #000' }} />
                        <p className="aquidedroit-signature-name">
                            Pr Abbé TEBAKABE ALOMO Donat
                        </p>
                        <p className="aquidedroit-signature-title">
                            {translations['Recteur']}
                        </p>
                    </div>
                </div>
                {qrCodeData && (
                    <div className="aquidedroit-qr-section">
                        <img
                            src={qrCodeData}
                            alt="QR Code"
                            style={{ width: '60px', height: '60px', border: '1px solid #000' }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttestationDirectionTemplate;

